import React, { useState, useEffect } from "react";
import "./style.css";
import { Link } from "react-router-dom";
import { useDialogs } from "../DialogContext";
import LoadingPage from "../LoadingPage";
import { backendUrl } from "../backendUrl";
import { validatePermissions } from "./validation";
import { Door, Key, Permission } from "./types";
import ConfiguratorContainer from "./ConfiguratorContainer";

// Typy Door, Key, Permission przeniesione do validation.ts

const LockConfigurator = () => {
  // K1: komunikaty i okno potwierdzenia zamiast systemowych alert / confirm
  const { notify, confirm } = useDialogs();
  const [pageIsLoaded, setPageIsLoaded] = useState<boolean>(false);
  const getDefaultLockValues = (lockType: string) => {
    switch (lockType) {
      case "N+G":
      case "DPZ":
      case "KPZ":
        return { lockOutside: 31, lockInside: 31 };
      case "HPZ":
        return { lockOutside: 31, lockInside: 0 };
      case "VHS":
      case "Briefkastenzylinder":
        return { lockOutside: 0, lockInside: 0 };
      default:
        return { lockOutside: 0, lockInside: 0 };
    }
  };

  const getStoredData = (key: string, defaultValue: any) => {
    const storedData = localStorage.getItem(key);
    return storedData ? JSON.parse(storedData) : defaultValue;
  };

  const storeData = (key: string, data: any) => {
    localStorage.setItem(key, JSON.stringify(data));
  };

  const [doors, setDoors] = useState<Door[]>(
    getStoredData("doors", [
      {
        id: 1,
        doorName: "Tür 1",
        lockAmount: 1,
        lockType: "DPZ",
        lockOutside: 31,
        lockInside: 31,
      },
      {
        id: 2,
        doorName: "Tür 2",
        lockAmount: 1,
        lockType: "HPZ",
        lockOutside: 31,
        lockInside: 0,
      },
      {
        id: 3,
        doorName: "Tür 3",
        lockAmount: 1,
        lockType: "KPZ",
        lockOutside: 31,
        lockInside: 31,
      },
    ])
  );
  const [keys, setKeys] = useState<Key[]>(
    getStoredData("keys", [
      {
        id: 1,
        keyName: "Schlüssel 1",
        amount: 1,
      },
      {
        id: 2,
        keyName: "Schlüssel 2",
        amount: 1,
      },
      {
        id: 3,
        keyName: "Schlüssel 3",
        amount: 1,
      },
    ])
  );
  const [permission, setPermission] = useState<Permission[]>(
    getStoredData("permission", [
      { id: 1, idKey: 1, idDoor: 1, allow: true },
      { id: 2, idKey: 2, idDoor: 2, allow: true },
      { id: 3, idKey: 3, idDoor: 3, allow: true },
    ])
  );

  useEffect(() => {
    // Zapisuj dane do localStorage po każdej zmianie
    try {
      storeData("doors", doors);
      storeData("keys", keys);
      storeData("permission", permission);
    } finally {
      setPageIsLoaded(true);
    }
  }, [doors, keys, permission]);

  const getNextDoorId = () => {
    return doors.length > 0 ? Math.max(...doors.map((door) => door.id)) + 1 : 1;
  };

  const getNextKeyId = () => {
    return keys.length > 0 ? Math.max(...keys.map((key) => key.id)) + 1 : 1;
  };

  const getNextPermissionId = () => {
    return permission.length > 0
      ? Math.max(...permission.map((perm) => perm.id)) + 1
      : 1;
  };

  const handleInputChange = (
    index: number,
    field: keyof Door,
    value: string | number,
    numb: number,
    step: number,
    name: string,
    innen: number,
    auben: number
  ) => {
    let dozmiany = numb + step;
    if (dozmiany === 26) {
      dozmiany = 27;
    }
    if (dozmiany === 37 || dozmiany === 36) {
      dozmiany = 35;
    }
    if (dozmiany === 30) {
      if (name === "HPZ") {
        dozmiany = 31;
      } else {
        dozmiany = 31;
      }
    }
    if (dozmiany > 120) {
      return 0;
    }
    if (dozmiany < 30 && (auben === 27 || innen === 27)) {
      return 0;
    }
    if (dozmiany === 32) {
      dozmiany = 31;
    }

    if (Number(dozmiany) < 27) {
      return 0;
    } else if (Number(dozmiany) < 31 && name === "HPZ") {
      return 0;
    } else {
      const updatedDoors = [...doors];
      updatedDoors[index][field] = value as never;

      if (field === "lockOutside" || field === "lockInside") {
        // Handle increment or decrement by 5
        updatedDoors[index][field] =
          (parseInt(updatedDoors[index][field].toString(), 10) || 0) + dozmiany;
      }

      setDoors(updatedDoors);
    }
  };

  const clearLocalStorage = async (e: React.MouseEvent) => {
    // Link do /1 nie może zadziałać przed odpowiedzią w oknie - po potwierdzeniu i tak
    // jest pełne przeładowanie strony (niżej)
    e.preventDefault();
    const confirmed = await confirm({
      message: "Möchten Sie wirklich alle Eingaben zurücksetzen?",
      confirmLabel: "Zurücksetzen",
      danger: true,
    });
    if (!confirmed) return;
    // Panel admina korzysta z tego samego localStorage - reset konfiguratora nie może wylogować admina
    const preserved = ["adminToken", "adminPermissions", "adminLang"].map(
      (key) => [key, localStorage.getItem(key)] as const
    );
    localStorage.clear();
    preserved.forEach(([key, value]) => {
      if (value !== null) localStorage.setItem(key, value);
    });
    window.location.reload();
  };

  const handleAddDoor = () => {
    setDoors([
      ...doors,
      {
        id: getNextDoorId(),
        doorName: `Tür ${getNextDoorId()}`,
        lockAmount: 1,
        lockType: "N+G",
        lockOutside: 31,
        lockInside: 31,
      },
    ]);
  };

  const handleAddKey = () => {
    setKeys([
      ...keys,
      {
        id: getNextKeyId(),
        keyName: `Schlüssel ${getNextKeyId()}`,
        amount: 1,
      },
    ]);
  };

  const handlePermissionChange = (
    idKey: number,
    idDoor: number,
    allow: boolean
  ) => {
    const existingPermissionIndex = permission.findIndex(
      (perm) => perm.idKey === idKey && perm.idDoor === idDoor
    );

    if (existingPermissionIndex !== -1) {
      // Permission already exists, remove it
      setPermission((prevPermission) => {
        const updatedPermission = [...prevPermission];
        updatedPermission.splice(existingPermissionIndex, 1);
        return updatedPermission;
      });
    } else {
      // Permission doesn't exist, add new permission
      setPermission((prevPermission) => [
        ...prevPermission,
        { id: getNextPermissionId(), idKey, idDoor, allow },
      ]);
    }
  };

  const handleDoorChange = (
    index: number,
    field: keyof Door,
    value: string | number
  ) => {
    const updatedDoors = [...doors];
    if (String(value) === "NaN") {
      value = 0;
    }
    if (field === "lockAmount" && Number(value) < 1) {
      notify("Die Menge darf nicht kleiner als 1 sein.", "info");
      updatedDoors[index][field] = 1 as never;
    } else {
      updatedDoors[index][field] = value as never;
    }

    if (field === "lockType") {
      // Aktualizuj wartości domyślne po zmianie typu zamka
      updatedDoors[index] = {
        ...updatedDoors[index],
        ...getDefaultLockValues(value as string),
      };
    }

    setDoors(updatedDoors);
  };
  const handleKeyChange = (
    index: number,
    field: keyof Key,
    value: number | string
  ) => {
    const updatedKeys = [...keys];
    if (String(value) === "NaN") {
      value = 0;
    }
    if (field === "amount" && Number(value) < 1) {
      notify("Die Menge darf nicht kleiner als 1 sein.", "info");
      updatedKeys[index][field] = 1 as never;
    } else {
      updatedKeys[index][field] = value as never;
    }
    setKeys(updatedKeys);
  };
  // validatePermissions importowana z validation.ts
  const handleNextPage = (e: React.MouseEvent) => {
    const { locksWithoutKey, keyWithoutPerm } = validatePermissions(doors, keys, permission);
    if (locksWithoutKey || keyWithoutPerm) {
      e.preventDefault();
      notify("Achtung: Zylinder oder Schlüssel ohne Schließberechtigung.", "error");
    } else {
      // Przejdź do następnej strony
    }
  };
const updateIdsAfterRemoval = (items: any[], removedIndex: number): any[] => {
  return items.map((item) => {
    if (item.id > removedIndex) {
      return { ...item, id: item.id - 1 };
    }
    return item;
  });
};

const updatePermissionIds = (
  permission: any[],
  removedIndex: number,
  idField: string
): any[] => {
  return permission.map((permi) => {
    if (permi[idField] > removedIndex) {
      return { ...permi, [idField]: permi[idField] - 1 };
    }
    return permi;
  });
};

const handleRemoveItem = (
  index: number,
  items: any[],
  setItems: (items: any[]) => void,
  permission: any[],
  setPermission: (permission: any[]) => void,
  permissionIdField: string
): void => {
  if (items.length > 1) {
    let updatedItems = items.filter((item) => item.id !== index);
    updatedItems = updateIdsAfterRemoval(updatedItems, index);

    let updatedPermission = permission.filter(
      (permi) => permi[permissionIdField] !== index
    );
    updatedPermission = updatePermissionIds(
      updatedPermission,
      index,
      permissionIdField
    );

    setPermission(updatedPermission);
    setItems(updatedItems);
  } else {
    notify("Es ist nicht möglich, alle Elemente zu entfernen.", "info");
  }
};

const handleRemoveKeyx = (index: number): void => {
  handleRemoveItem(index, keys, setKeys, permission, setPermission, "idKey");
};

const handleRemoveDoorx = (index: number): void => {
  handleRemoveItem(index, doors, setDoors, permission, setPermission, "idDoor");
};
  return (
    <>
       {pageIsLoaded ? (
      <div className="configurator-wrapper">
        <div className="configurator-main">
          <ConfiguratorContainer
            doors={doors}
            keys={keys}
            permission={permission}
            handleAddDoor={handleAddDoor}
            handleAddKey={handleAddKey}
            handleRemoveKeyx={handleRemoveKeyx}
            handleKeyChange={handleKeyChange}
            handleRemoveDoorx={handleRemoveDoorx}
            handleDoorChange={handleDoorChange}
            handleInputChange={handleInputChange}
            getDefaultLockValues={getDefaultLockValues}
            handlePermissionChange={handlePermissionChange}
            backendUrl={backendUrl}
          />
        </div>

          <div className="LockConfiguratorButonsXD">
          <Link to={"/1"}>
            <span onClick={clearLocalStorage}>Alles zurücksetzen</span>
          </Link>
          <Link to="/2" onClick={handleNextPage}>
            <div className="LockConfiguratorButon">
              <div className="text-wrapper">Ergebnisse anzeigen</div>
              <img className="vector" alt="Vector" src={`${backendUrl}/img/Vector.svg`} />
              </div>
            </Link>
          </div>
        </div>
      ) : (
        <LoadingPage />
      )}
    </>
  );
};

export default LockConfigurator;